/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import axios from "axios";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import Card from "components/card";
import { Button } from "@chakra-ui/react";
import { TokenManager } from "utils/tokenManager";

const UserUpdateStatusConfirmModal: React.FC<{
  setSwalProps: any;
  triggerReload(): void;
  isOpen: boolean;
  onClose(): void;
  userToUpdate: any;
  statusToUpdate: any;
}> = ({
  setSwalProps,
  triggerReload,
  isOpen,
  onClose,
  userToUpdate,
  statusToUpdate,
}) => {
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const userRole = localStorage.getItem("userRole")?.toLowerCase();
    const sysUsername = localStorage.getItem("username")?.toLowerCase();

    const closeModal = () => {
      onClose();
    };

    const handleSubmit = async () => {
      setIsProcessing(true);
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/${userRole}/user/action/update-user-status`,
          {
            userRole: userRole,
            sysUsername: sysUsername,
            subaccountId: null,
            username: userToUpdate.username,
            email: userToUpdate.email,
            status: statusToUpdate === "active" ? "Active" : "Inactive",
          }
        );

        // Close modal first, then show success message
        setIsProcessing(false);
        onClose();

        setSwalProps({
          show: true,
          icon: "success",
          title: "Success",
          html: `User has been ${statusToUpdate === "active" ? "activated" : "deactivated"}`,
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            triggerReload();
          },
        });
      } catch (error: any) {
        setIsProcessing(false);
        onClose();

        console.error("Error proceeding:", error.response?.data);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "User status update failed. Please try again or contact support",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
          },
        });
      }
    };

    return (
      <>
        {userToUpdate && (
          <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
              <ModalBody className="p-6 md:p-7">
                <div className="mb-4 flex items-start gap-3">
                  <div className={`rounded-xl p-2 ring-1 ${
                    statusToUpdate === "active" 
                      ? "bg-green-50 text-green-600 ring-green-100 dark:bg-green-900/20 dark:text-green-400 dark:ring-green-900/30"
                      : "bg-red-50 text-red-600 ring-red-100 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-900/30"
                  }`}>
                    <svg
                      viewBox="0 0 24 24"
                      className="h-5 w-5"
                      fill="currentColor"
                    >
                      {statusToUpdate === "active" ? (
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      ) : (
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z" />
                      )}
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {statusToUpdate === "active" ? "Activate" : "Deactivate"} User
                    </h1>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      Update user account status
                    </p>
                  </div>
                </div>

                <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
                  statusToUpdate === "active"
                    ? "border-green-200 bg-green-50 text-green-800 dark:border-green-900/40 dark:bg-green-900/20 dark:text-green-300"
                    : "border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
                }`}>
                  Please confirm if you wish to <span className="font-semibold">{statusToUpdate === "active" ? "activate" : "deactivate"}</span> the user.
                </div>

                <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                  <div className="flex flex-col gap-3 text-sm">
                    <div className="flex">
                      <div className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                        Email
                      </div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {userToUpdate.email}
                      </div>
                    </div>

                    <div className="flex">
                      <div className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                        Username
                      </div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {userToUpdate.username}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => closeModal()}
                    isDisabled={isProcessing}
                    className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={() => handleSubmit()}
                    isDisabled={isProcessing || TokenManager.isImpersonating()}
                    isLoading={isProcessing}
                    loadingText="Processing..."
                    className={`rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-400 ${
                      statusToUpdate === "active"
                        ? "bg-green-600 hover:bg-green-500 focus:ring-green-500/30"
                        : "bg-red-600 hover:bg-red-500 focus:ring-red-500/30"
                    }`}
                  >
                    Confirm
                  </Button>
                </div>
              </ModalBody>
            </ModalContent>
          </Modal>
        )}
      </>
    );
  };

export default UserUpdateStatusConfirmModal;